import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { type TTheme } from "./theme.types";
import { useStore } from "../../../store";
import { getRootStyles } from "../../../utils";

export type TThemeProviderProps = {
  children: ReactNode;
  defaultTheme?: TTheme;
  storageKey?: string;
};

export type TThemeProviderState = {
  theme: TTheme;
  setTheme: (theme: TTheme) => void;
};

// eslint-disable-next-line react-refresh/only-export-components
export const ThemeProviderContext = createContext<TThemeProviderState | null>(
  null,
);

export const ThemeProvider = ({
  children,
  defaultTheme = "system",
  storageKey = "ui-theme",
}: TThemeProviderProps) => {
  const [theme, setTheme] = useState<TTheme>(
    () => (localStorage.getItem(storageKey) as TTheme) || defaultTheme,
  );
  const setBrandingList = useStore((state) => state.setBrandingList);
  const brandingList = useStore((state) => state.brandingList);

  useEffect(() => {
    const root = document.documentElement;
    if (
      theme === "dark" ||
      (theme === "system" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches)
    ) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [theme]);

  // 2️⃣ Seed defaults from CSS variables once
  useEffect(() => {
    if (!brandingList?.primary_color_light) {
      setBrandingList({
        primary_color_light: getRootStyles("--primary-light"),
        primary_color_dark: getRootStyles("--primary-dark"),
        secondary_color_light: getRootStyles("--secondary"),
        secondary_color_dark: getRootStyles("--secondary"),
      });
    }
  }, [brandingList?.primary_color_light, setBrandingList]);

  const value = {
    theme,
    setTheme: useCallback(
      (theme: TTheme) => {
        localStorage.setItem(storageKey, theme);
        setTheme(theme);
      },
      [storageKey],
    ),
  };

  return (
    <ThemeProviderContext.Provider value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (!context) throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
